/**
 * Text derivation for the clip cards' transcript preview: a short opener shown
 * on the card itself, and a longer excerpt for the hover panel. Both read
 * `video.transcriptionText` — the verbatim clip transcript already present in
 * the grid payload — so neither ever needs a fetch. For a reel that text is the
 * sections' texts joined with blank lines (`combineClipTexts`), which the
 * excerpt preserves as paragraphs and the one-line snippet flattens away.
 */

/** Overfills two --text-xs lines at the card's maximum content width. */
export const SNIPPET_MAX_CHARS = 220;

/** ≈12 lines at --text-xs / 1.5 line-height in the 300px hover panel. */
export const EXCERPT_MAX_CHARS = 600;

/**
 * First words of a transcript, whitespace-collapsed to a single line, for the
 * card's 2-line CSS clamp. No trailing ellipsis — the clamp supplies it.
 * Returns '' for missing/blank text (untranscribed clips).
 */
export function transcriptSnippet(text: string | undefined | null): string {
  if (!text) return '';
  // Transcripts can run to tens of KB; bound the regex work before collapsing.
  return text
    .slice(0, SNIPPET_MAX_CHARS * 4)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SNIPPET_MAX_CHARS);
}

/**
 * Transcript excerpt as paragraphs for the hover panel. Blank-line breaks (a
 * reel's section joins) become separate paragraphs; the total is capped at
 * `maxChars`, cutting the overflowing paragraph at a word boundary with '…'
 * (the `firstLineOf` idiom from deriveReelSections).
 */
export function transcriptParagraphs(
  text: string | undefined | null,
  maxChars = EXCERPT_MAX_CHARS,
): string[] {
  if (!text) return [];
  const paragraphs = text
    .slice(0, maxChars * 2)
    .split(/\n\s*\n/)
    .map(p => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const result: string[] = [];
  let used = 0;
  for (const paragraph of paragraphs) {
    if (used + paragraph.length <= maxChars) {
      result.push(paragraph);
      used += paragraph.length;
      continue;
    }
    const room = maxChars - used;
    if (room > 0) {
      const cut = paragraph.slice(0, room);
      const lastSpace = cut.lastIndexOf(' ');
      const clipped = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd();
      if (clipped) result.push(`${clipped}…`);
    }
    break;
  }
  return result;
}
